"use client";

import { useState, useCallback, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { Label } from "@/app/components/ui/label";
import { Badge } from "@/app/components/ui/badge";
import { Alert, AlertDescription } from "@/app/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/app/components/ui/select";
import { Slider } from "@/app/components/ui/slider";
import { Checkbox } from "@/app/components/ui/checkbox";
import { CopyButton } from "@/app/components/ui/copy-button";
import { 
  Hash, 
  Copy, 
  Download, 
  RotateCcw, 
  Settings, 
  Info, 
  AlertTriangle, 
  CheckCircle,
  Key,
  Shield,
  Zap,
  BarChart3,
  History,
  Save,
  Trash2,
  RefreshCw,
  Database,
  Lock,
  Clock,
  Globe,
  Fingerprint,
  Layers,
  Target,
  Sparkles,
  Atom,
  Link,
  Code
} from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";

interface GeneratedNanoID {
  id: string;
  value: string;
  timestamp: Date;
  length: number;
  type: string;
  settings: {
    size: number;
    alphabet?: string;
    prefix?: string;
  };
}

interface NanoIDHistory {
  id: string;
  nanoids: string[];
  timestamp: Date;
  count: number;
  settings: any;
}

type GeneratorType = "standard" | "custom-size" | "prefixed" | "custom-alphabet";

// Client-side alphabet definitions (kept for reference, actual generation is done server-side via TRPC)
const ALPHABETS = {
  urlSafe: "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-",
  noLookalikes: "23456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz",
  numbers: "0123456789",
  lowercase: "abcdefghijklmnopqrstuvwxyz",
  uppercase: "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
  alphanumeric: "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789",
  hex: "0123456789abcdef",
  binary: "01",
  base64: "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/",
  custom: ""
};

export default function NanoIDGeneratorPage() {
  const [generatedNanoIDs, setGeneratedNanoIDs] = useState<GeneratedNanoID[]>([]);
  const [history, setHistory] = useState<NanoIDHistory[]>([]);
  const [activeTab, setActiveTab] = useState<GeneratorType>("standard");
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  // Standard settings
  const [count, setCount] = useState(1);
  const [size, setSize] = useState(21);

  // Custom size settings
  const [customSize, setCustomSize] = useState(21);

  // Prefixed settings
  const [prefix, setPrefix] = useState("");
  const [prefixedSize, setPrefixedSize] = useState(21);

  // Custom alphabet settings
  const [selectedAlphabet, setSelectedAlphabet] = useState("urlSafe");
  const [customAlphabet, setCustomAlphabet] = useState("");
  const [alphabetSize, setAlphabetSize] = useState(21);

  // TRPC mutations
  const generateStandardMutation = trpc.nanoid.generateStandard.useMutation();
  const generatePrefixedMutation = trpc.nanoid.generatePrefixed.useMutation();
  const generateCustomAlphabetMutation = trpc.nanoid.generateCustomAlphabet.useMutation();
  const generateMutation = trpc.nanoid.generate.useMutation();

  const generateNanoIDs = useCallback(async () => {
    try {
      let result: any;
      let type = "";
      let settings: any = {};

      switch (activeTab) {
        case "standard":
          result = await generateStandardMutation.mutateAsync({
            count,
            size
          });
          type = `Standard NanoID (${size} chars)`;
          settings = { size, count };
          break;
        case "custom-size":
          result = await generateMutation.mutateAsync({
            count,
            size: customSize
          });
          type = `Custom Size (${customSize} chars)`;
          settings = { size: customSize, count };
          break;
        case "prefixed":
          result = await generatePrefixedMutation.mutateAsync({
            count,
            size: prefixedSize,
            prefix
          });
          type = `Prefixed (${prefix}${prefixedSize} chars)`;
          settings = { size: prefixedSize, prefix, count };
          break;
        case "custom-alphabet":
          result = await generateCustomAlphabetMutation.mutateAsync({
            count,
            size: alphabetSize,
            alphabet: selectedAlphabet === "custom" ? customAlphabet : "",
            alphabetType: selectedAlphabet as any
          });
          type = `Custom Alphabet (${alphabetSize} chars)`;
          settings = { size: alphabetSize, alphabet: selectedAlphabet === "custom" ? customAlphabet : selectedAlphabet, count };
          break;
      }

      if (result.success) {
        const nanoids = result.nanoids;

        // Add to generated NanoIDs
        const newGeneratedNanoIDs = nanoids.map((value: string, index: number) => ({
          id: `${Date.now()}-${index}`,
          value,
          timestamp: new Date(),
          length: value.length,
          type,
          settings
        }));

        setGeneratedNanoIDs(prev => [...newGeneratedNanoIDs, ...prev]);

        // Add to history
        const historyEntry: NanoIDHistory = {
          id: Date.now().toString(),
          nanoids,
          timestamp: new Date(),
          count: nanoids.length,
          settings
        };

        setHistory(prev => [historyEntry, ...prev.slice(0, 49)]); // Keep last 50 entries

        toast.success(`Generated ${nanoids.length} NanoID${nanoids.length > 1 ? 's' : ''}!`);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to generate NanoIDs";
      toast.error(errorMessage);
    }
  }, [activeTab, count, size, customSize, prefix, prefixedSize, selectedAlphabet, customAlphabet, alphabetSize, generateStandardMutation, generatePrefixedMutation, generateCustomAlphabetMutation, generateMutation]);

  const copyToClipboard = useCallback(async (text: string, index?: number) => {
    try {
      await navigator.clipboard.writeText(text);
      if (index !== undefined) {
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
      }
      toast.success("Copied to clipboard!");
    } catch (error) {
      toast.error("Failed to copy to clipboard");
    }
  }, []);

  const clearHistory = useCallback(() => {
    setHistory([]);
    setGeneratedNanoIDs([]);
    toast.success("History cleared!");
  }, []);

  const exportNanoIDs = useCallback(() => {
    const data = {
      generated: generatedNanoIDs,
      history: history,
      timestamp: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `nanoid-export-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success("NanoIDs exported!");
  }, [generatedNanoIDs, history]);

  const validateAlphabet = useCallback((alphabet: string) => {
    if (alphabet.length === 0) {
      return "Alphabet cannot be empty";
    }
    if (alphabet.length > 256) {
      return "Alphabet cannot exceed 256 characters";
    }
    const uniqueChars = new Set(alphabet).size;
    if (uniqueChars !== alphabet.length) {
      return "Alphabet must contain unique characters only";
    }
    return null;
  }, []);

  const getTotalLength = useMemo(() => {
    return generatedNanoIDs.reduce((sum, item) => sum + item.length, 0);
  }, [generatedNanoIDs]);

  const getAverageLength = useMemo(() => {
    if (generatedNanoIDs.length === 0) return 0;
    return getTotalLength / generatedNanoIDs.length;
  }, [generatedNanoIDs, getTotalLength]);

  const getUniqueCount = useMemo(() => {
    const uniqueValues = new Set(generatedNanoIDs.map(item => item.value));
    return uniqueValues.size;
  }, [generatedNanoIDs]);

  const alphabetError = selectedAlphabet === "custom" ? validateAlphabet(customAlphabet) : null;

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">NanoID Generator</h1>
        <p className="text-muted-foreground">
          Generate tiny, secure, URL-friendly unique string IDs. NanoID is more compact than UUID and faster than CUID2.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Generator Controls */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Atom className="h-5 w-5" />
                NanoID Generator
              </CardTitle>
              <CardDescription>
                Choose your generator type and configure the settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as GeneratorType)}>
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="standard">Standard</TabsTrigger>
                  <TabsTrigger value="custom-size">Custom Size</TabsTrigger>
                  <TabsTrigger value="prefixed">Prefixed</TabsTrigger>
                  <TabsTrigger value="custom-alphabet">Custom Alphabet</TabsTrigger>
                </TabsList>

                <TabsContent value="standard" className="space-y-4">
                  <div className="space-y-2">
                    <Label>Size: {size} characters</Label>
                    <Slider
                      value={[size]}
                      onValueChange={(value) => setSize(value[0])}
                      max={100}
                      min={1}
                      step={1}
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Count: {count}</Label>
                    <Slider
                      value={[count]}
                      onValueChange={(value) => setCount(value[0])}
                      max={100}
                      min={1}
                      step={1}
                      className="w-full"
                    />
                  </div>
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      Standard NanoID uses URL-safe characters (A-Z, a-z, 0-9, _, -) and is optimized for performance.
                    </AlertDescription>
                  </Alert>
                </TabsContent>

                <TabsContent value="custom-size" className="space-y-4">
                  <div className="space-y-2">
                    <Label>Custom Size: {customSize} characters</Label>
                    <Slider
                      value={[customSize]}
                      onValueChange={(value) => setCustomSize(value[0])}
                      max={100}
                      min={1}
                      step={1}
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Count: {count}</Label>
                    <Slider
                      value={[count]}
                      onValueChange={(value) => setCount(value[0])}
                      max={100}
                      min={1}
                      step={1}
                      className="w-full"
                    />
                  </div>
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      Custom size NanoID allows you to specify any length between 1 and 100 characters.
                    </AlertDescription>
                  </Alert>
                </TabsContent>

                <TabsContent value="prefixed" className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="prefix">Prefix</Label>
                    <Input
                      id="prefix"
                      placeholder="user_"
                      value={prefix}
                      onChange={(e) => setPrefix(e.target.value)}
                      maxLength={20}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>NanoID Size: {prefixedSize} characters</Label>
                    <Slider
                      value={[prefixedSize]}
                      onValueChange={(value) => setPrefixedSize(value[0])}
                      max={100}
                      min={1}
                      step={1}
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Count: {count}</Label>
                    <Slider
                      value={[count]}
                      onValueChange={(value) => setCount(value[0])}
                      max={100}
                      min={1}
                      step={1}
                      className="w-full"
                    />
                  </div>
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      Prefixed NanoID adds a custom prefix to make your identifiers more descriptive (e.g., user_abc123...).
                    </AlertDescription>
                  </Alert>
                </TabsContent>

                <TabsContent value="custom-alphabet" className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="alphabet">Alphabet</Label>
                    <Select value={selectedAlphabet} onValueChange={setSelectedAlphabet}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="urlSafe">URL Safe (A-Z, a-z, 0-9, _, -)</SelectItem>
                        <SelectItem value="noLookalikes">No Lookalikes (23456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz)</SelectItem>
                        <SelectItem value="numbers">Numbers Only (0-9)</SelectItem>
                        <SelectItem value="lowercase">Lowercase (a-z)</SelectItem>
                        <SelectItem value="uppercase">Uppercase (A-Z)</SelectItem>
                        <SelectItem value="alphanumeric">Alphanumeric (A-Z, a-z, 0-9)</SelectItem>
                        <SelectItem value="hex">Hexadecimal (0-9, a-f)</SelectItem>
                        <SelectItem value="binary">Binary (0-1)</SelectItem>
                        <SelectItem value="base64">Base64 (A-Z, a-z, 0-9, +, /)</SelectItem>
                        <SelectItem value="custom">Custom Alphabet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {selectedAlphabet === "custom" && (
                    <div className="space-y-2">
                      <Label htmlFor="customAlphabet">Custom Alphabet</Label>
                      <Input
                        id="customAlphabet"
                        placeholder="ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"
                        value={customAlphabet}
                        onChange={(e) => setCustomAlphabet(e.target.value)}
                      />
                      {alphabetError && (
                        <Alert variant="destructive">
                          <AlertTriangle className="h-4 w-4" />
                          <AlertDescription>{alphabetError}</AlertDescription>
                        </Alert>
                      )}
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Size: {alphabetSize} characters</Label>
                    <Slider
                      value={[alphabetSize]}
                      onValueChange={(value) => setAlphabetSize(value[0])}
                      max={100}
                      min={1}
                      step={1}
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Count: {count}</Label>
                    <Slider
                      value={[count]}
                      onValueChange={(value) => setCount(value[0])}
                      max={100}
                      min={1}
                      step={1}
                      className="w-full"
                    />
                  </div>
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      Choose from predefined alphabets or create your own custom character set.
                    </AlertDescription>
                  </Alert>
                </TabsContent>
              </Tabs>

              <div className="flex gap-2">
                <Button 
                  onClick={generateNanoIDs} 
                  className="flex-1"
                  disabled={
                    activeTab === "custom-alphabet" && selectedAlphabet === "custom" && !!alphabetError ||
                    generateStandardMutation.isPending ||
                    generatePrefixedMutation.isPending ||
                    generateCustomAlphabetMutation.isPending ||
                    generateMutation.isPending
                  }
                >
                  {(generateStandardMutation.isPending ||
                    generatePrefixedMutation.isPending ||
                    generateCustomAlphabetMutation.isPending ||
                    generateMutation.isPending) ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Atom className="h-4 w-4 mr-2" />
                      Generate NanoIDs
                    </>
                  )}
                </Button>
                <Button variant="outline" onClick={clearHistory}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clear
                </Button>
                <Button variant="outline" onClick={exportNanoIDs}>
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Generated NanoIDs Display */}
          {generatedNanoIDs.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="h-5 w-5" />
                  Generated NanoIDs
                </CardTitle>
                <CardDescription>
                  {generatedNanoIDs.length} NanoID{generatedNanoIDs.length > 1 ? 's' : ''} generated
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 mb-4">
                  {generatedNanoIDs.slice(0, 20).map((item, index) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-3 border rounded-lg bg-muted/50"
                    >
                      <div className="flex items-center gap-3">
                        <Badge variant="secondary" className="text-xs">
                          {item.length} chars
                        </Badge>
                        <code className="text-sm font-mono break-all">{item.value}</code>
                      </div>
                      <CopyButton
                        text={item.value}
                        onCopy={() => copyToClipboard(item.value, index)}
                        className="h-8 w-8"
                      />
                    </div>
                  ))}
                </div>
                {generatedNanoIDs.length > 20 && (
                  <p className="text-sm text-muted-foreground">
                    Showing first 20 NanoIDs. Total: {generatedNanoIDs.length}
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Statistics and History */}
        <div className="space-y-6">
          {/* Statistics */}
          {generatedNanoIDs.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Statistics
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{generatedNanoIDs.length}</div>
                    <div className="text-sm text-muted-foreground">Total NanoIDs</div>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{getUniqueCount}</div>
                    <div className="text-sm text-muted-foreground">Unique</div>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{getAverageLength.toFixed(1)}</div>
                    <div className="text-sm text-muted-foreground">Avg Length</div>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{getTotalLength}</div>
                    <div className="text-sm text-muted-foreground">Total Chars</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* History */}
          {history.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="h-5 w-5" />
                  Recent History
                </CardTitle>
                <CardDescription>
                  Last {history.length} generation{history.length > 1 ? 's' : ''}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {history.slice(0, 10).map((entry) => (
                    <div
                      key={entry.id}
                      className="p-3 border rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="secondary" className="text-xs">
                          {entry.count} NanoIDs
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {new Date(entry.timestamp || Date.now()).toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="space-y-1">
                        {entry.nanoids.slice(0, 3).map((nanoid, index) => (
                          <code key={index} className="text-xs font-mono block break-all">
                            {nanoid}
                          </code>
                        ))}
                        {entry.nanoids.length > 3 && (
                          <span className="text-xs text-muted-foreground">
                            +{entry.nanoids.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* NanoID Benefits */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                NanoID Benefits
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-start gap-2">
                <Zap className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium">Ultra Fast</div>
                  <div className="text-muted-foreground">2x faster than UUID, 3x faster than CUID2</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Link className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium">URL Safe</div>
                  <div className="text-muted-foreground">Safe to use in URLs without encoding</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Fingerprint className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium">Compact</div>
                  <div className="text-muted-foreground">Only 21 characters (vs 36 for UUID)</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Lock className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium">Cryptographically Secure</div>
                  <div className="text-muted-foreground">Uses crypto.getRandomValues()</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Code className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium">Zero Dependencies</div>
                  <div className="text-muted-foreground">No external dependencies required</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tips */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="h-4 w-4" />
                Tips
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span>Use "No Lookalikes" alphabet to avoid confusing characters (0/O, 1/I/l)</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span>NanoIDs are perfect for database primary keys and API endpoints</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span>Shorter IDs (10-15 chars) are great for user-facing identifiers</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span>Custom alphabets allow domain-specific character sets</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
